// Métricas derivadas del Home VL — lógica PURA, sin React.
//
// Todo lo que dependa de la meta devuelve `null` cuando la meta no existe, igual que en Tiendas.
// El tablero es donde más duele confundir `null` con `0`: un "0% de la meta" a media tarde manda a
// corregir un problema que nadie tiene.
import type { SalesKpisResponse, SalesMixSlice } from '@/api/services/retail-wizard/backoffice';
import { mxClock } from '@/lib/dateHelpers';
import { FLOOR_CLOSING_HOUR, FLOOR_OPENING_HOUR } from '../constants';

export interface NetworkGoal {
    /** Suma de las metas diarias de las tiendas activas con meta; `null` si ninguna tiene. */
    goalCents: number | null;
    /** Lo vendido hoy por ESAS mismas tiendas; `null` si alguna no trae su vendido. */
    soldCents: number | null;
    /** Tiendas activas con meta. */
    storesWithGoal: number;
    /** Tiendas activas en total. */
    activeStores: number;
}

/**
 * Meta del día de la red y lo vendido por las tiendas que la componen, para que el % compare peras
 * con peras. Con `activeStoreIds` en `null` (padrón caído) no filtra por estado.
 */
export function networkGoal(
    stores:
        | readonly {
              storeId: string;
              dailyGoalCents: number | null;
              soldTodayCents: number | null;
          }[]
        | undefined,
    activeStoreIds: ReadonlySet<string> | null,
): NetworkGoal {
    const active = (stores ?? []).filter(
        (row) => activeStoreIds === null || activeStoreIds.has(row.storeId),
    );
    const withGoal = active.filter(
        (row): row is typeof row & { dailyGoalCents: number } => row.dailyGoalCents !== null,
    );
    const soldKnown = withGoal.every((row) => row.soldTodayCents !== null);
    return {
        goalCents:
            withGoal.length === 0
                ? null
                : withGoal.reduce((sum, row) => sum + row.dailyGoalCents, 0),
        soldCents:
            withGoal.length === 0 || !soldKnown
                ? null
                : withGoal.reduce((sum, row) => sum + (row.soldTodayCents ?? 0), 0),
        storesWithGoal: withGoal.length,
        activeStores: active.length,
    };
}

/** Cumplimiento de la meta del día, o `null` si no hay meta. */
export function networkGoalProgress(
    soldCents: number | null,
    goalCents: number | null,
): number | null {
    if (soldCents === null || goalCents === null || goalCents <= 0) return null;
    return soldCents / goalCents;
}

/** Cuánto falta para la meta, en centavos. `null` sin meta; nunca negativo. */
export function goalRemainingCents(
    soldCents: number | null,
    goalCents: number | null,
): number | null {
    if (soldCents === null || goalCents === null) return null;
    return Math.max(0, goalCents - soldCents);
}

/** Participación de SureKeep en las piezas del día, como fracción. `null` si no hubo ventas. */
export function surekeepMixShare(kpis: SalesKpisResponse): number | null {
    if (kpis.unitsToday <= 0) return null;
    return kpis.surekeepUnitsToday / kpis.unitsToday;
}

/**
 * Cómo vamos contra el mismo día de la semana pasada, como fracción.
 * `null` si no hay línea base o si la base es cero (dividir por cero no es "infinito por ciento").
 */
export function baselineChange(kpis: SalesKpisResponse): number | null {
    const base = kpis.baselineSameDayLastWeekCents;
    if (base === null || base <= 0) return null;
    return (kpis.soldTodayCents - base) / base;
}

/** Hora del día en el piso (CDMX) como fracción: 15:30 → 15.5. */
function floorClock(now: Date): number {
    const { hours, minutes } = mxClock(now);
    return hours + minutes / 60;
}

/** Horas que faltan para el cierre del piso, en hora de CDMX. 0 si el piso ya cerró. */
export function hoursToClosing(now: Date = new Date()): number {
    const remaining = FLOOR_CLOSING_HOUR - floorClock(now);
    return Math.max(0, Math.round(remaining * 10) / 10);
}

/** Horas que lleva abierto el piso en hora de CDMX; 0 antes de la apertura. */
export function hoursSinceOpening(now: Date = new Date()): number {
    return Math.max(0, floorClock(now) - FLOOR_OPENING_HOUR);
}

/** La hora del piso (CDMX) en `HH:mm hrs`, el sub del KPI "Turno". */
export function nowHhMm(now: Date = new Date()): string {
    const { hours, minutes } = mxClock(now);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} hrs`;
}

/** El mix ordenado de mayor a menor, con su participación calculada. */
export function mixWithShare(mix: SalesMixSlice[]): { slice: SalesMixSlice; share: number }[] {
    const total = mix.reduce((s, m) => s + m.salesCount, 0);
    return [...mix]
        .sort((a, b) => b.salesCount - a.salesCount)
        .map((slice) => ({ slice, share: total > 0 ? slice.salesCount / total : 0 }));
}

/** `+12%` / `-8%` / `—`. Mismo criterio que en el resto del portal. */
export function formatPercentDelta(change: number | null): string {
    if (change === null) return '—';
    const pct = Math.round(change * 100);
    return `${pct >= 0 ? '+' : ''}${String(pct)}%`;
}

/**
 * Cuántas filas vendieron hoy, sobre cuántas hay. Es la fórmula del mock para "Piso activo".
 * `active` es `null` cuando NINGUNA fila trae el conteo: ahí un `0` diría "hoy no vendió nadie".
 */
export function activeShare<T>(
    rows: T[] | undefined,
    unitsOf: (row: T) => number | null,
): { active: number | null; total: number } {
    const list = rows ?? [];
    const known = list.filter((row) => unitsOf(row) !== null);
    return {
        active: known.length === 0 ? null : known.filter((row) => (unitsOf(row) ?? 0) > 0).length,
        total: list.length,
    };
}

/**
 * Lo que el piso tiene que vender por hora para alcanzar la meta antes del cierre.
 * `null` sin meta o con el piso ya cerrado — dividir entre 0 horas no es "infinito por hora".
 */
export function goalPacePerHourCents(remainingCents: number | null, hours: number): number | null {
    if (remainingCents === null || hours <= 0) return null;
    return Math.round(remainingCents / hours);
}

/** Excedente sobre la meta en centavos, o `null` si no hay meta o todavía no se alcanzó. */
export function goalSurplusCents(
    soldCents: number | null,
    goalCents: number | null,
): number | null {
    if (soldCents === null || goalCents === null) return null;
    const surplus = soldCents - goalCents;
    return surplus > 0 ? surplus : null;
}

/** `$438.6K` / `$720` / `—`. El formato corto de la barra de KPIs del mock. */
export function formatMoneyShort(cents: number | null): string {
    if (cents === null) return '—';
    const pesos = cents / 100;
    if (Math.abs(pesos) >= 1000) return `$${(pesos / 1000).toFixed(1)}K`;
    return `$${String(Math.round(pesos))}`;
}
